import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import {
  AiSettingsFormValues,
  aiSettingsFormSchema,
  isSameSettings,
} from '../utils/aiSettingsForm';

type UseAiSettingsFormReturn = {
  form: UseFormReturn<AiSettingsFormValues>;
  /**
   * Settings saved by someone else since the form was loaded, while it holds
   * changes of its own. Unset when there is nothing to choose between.
   */
  latestValues: AiSettingsFormValues | undefined;
  /** Throws the unsaved changes away for the latest settings. */
  loadLatest: () => void;
};

/**
 * The form over the settings on screen, following them as they change on the
 * server.
 *
 * With nothing edited, a change just lands in the form. With edits, it would
 * throw them away, so it is held back and offered instead — unless the edits
 * already match it, as when the change is the echo of this agent's own save.
 */
export function useAiSettingsForm(
  serverValues: AiSettingsFormValues,
): UseAiSettingsFormReturn {
  const form = useForm<AiSettingsFormValues>({
    defaultValues: serverValues,
    resolver: zodResolver(aiSettingsFormSchema),
    mode: 'all',
  });
  const { isDirty, defaultValues } = form.formState;
  const [latestValues, setLatestValues] = useState<AiSettingsFormValues>();

  useEffect(() => {
    const loaded = defaultValues as AiSettingsFormValues | undefined;
    if (loaded && isSameSettings(serverValues, loaded)) {
      setLatestValues(undefined);
      return;
    }

    if (!isDirty || isSameSettings(serverValues, form.getValues())) {
      form.reset(serverValues);
      setLatestValues(undefined);
    } else {
      setLatestValues(serverValues);
    }
    // Only a change on the server is news; edits are judged when one comes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValues]);

  const loadLatest = () => {
    if (!latestValues) return;
    form.reset(latestValues);
    setLatestValues(undefined);
  };

  return { form, latestValues, loadLatest };
}
